import React, {FC, ReactNode} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {IconButton} from '../../../../../libs/shared/ui/src/components/buttons/iconButton';
import {DropdownItem} from '../../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItem';
import {SimpleDropdown} from '../../../../../libs/shared/ui/src/components/dropdowns/simpleDropdown/simpleDropdown';
import {Icon, IconName} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {
  PopoverCoordinator,
  PopoverTransferProps,
} from '../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {UserThemeConsumer} from '../../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {useUserTheme} from '../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {AccountModel} from '../../../../models/api/accountModel';

/*
 * Props.
 */

interface ContactCardFooterSearchHistoryProps {
  account?: AccountModel;
  onSearchContactHistory: () => void;
  onSearchAccountHistory: (arg: string) => void;
}

/*
 * Styles.
 */

const StyledDropdownItemDiv = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  gap: 12px;

  padding-left: 12px;
`;

const StyledContentDiv = styled.div`
  white-space: nowrap;
`;

/*
 * Component.
 */

export const ContactCardFooterSearchHistory: FC<ContactCardFooterSearchHistoryProps> = ({
  account,
  onSearchContactHistory,
  onSearchAccountHistory,
}) => {
  const theme = useUserTheme();
  // If no account is present the button will not open a popover, and it will directly perform a contact history search.
  if (!account) {
    return (
      <IconButton
        iconName="history"
        color={theme.greys.shade70}
        hoverColor={theme.greys.shade80}
        placement="top"
        label={<Trans id="contact-card-footer-action-search-history">Search contact history</Trans>}
        onClick={onSearchContactHistory}
      />
    );
  }

  return (
    <PopoverCoordinator
      renderButton={(hasHover) => renderButton(hasHover)}
      renderModal={(popoverProps) =>
        renderDropdown(popoverProps, account, onSearchContactHistory, onSearchAccountHistory)
      }
    />
  );
};

/*
 * Helpers.
 */

function renderButton(hasHover: boolean) {
  return (
    <UserThemeConsumer>
      {(theme) => (
        <IconButton
          iconName="history"
          color={theme.greys.shade70}
          hoverColor={theme.greys.shade80}
          placement="top"
          label={<Trans id="contact-card-footer-action-search-history-popover">Search history</Trans>}
          hasHover={hasHover}
        />
      )}
    </UserThemeConsumer>
  );
}

function renderDropdown(
  popoverProps: PopoverTransferProps,
  account: AccountModel | undefined,
  onSearchContactHistory: () => void,
  onSearchAccountHistory: (arg: string) => void,
) {
  return (
    <SimpleDropdown {...popoverProps} width={155} placement="bottom-end">
      {renderDropdownItem(
        'contactsSmall',
        <Trans id="contact-card-footer-action-search-history-contact">Contact history</Trans>,
        onSearchContactHistory,
      )}
      {account &&
        renderDropdownItem(
          'briefcaseOutlineSmall',
          <Trans id="contact-card-footer-action-search-history-account">Account history</Trans>,
          () => onSearchAccountHistory(account.alias ?? account.name),
        )}
    </SimpleDropdown>
  );
}

function renderDropdownItem(iconName: IconName, content: ReactNode, onSelect: () => void) {
  return (
    <DropdownItem onSelect={onSelect}>
      <StyledDropdownItemDiv>
        <Icon name={iconName} />
        <StyledContentDiv>{content}</StyledContentDiv>
      </StyledDropdownItemDiv>
    </DropdownItem>
  );
}
